import { Box, Button, Skeleton, Typography, useTheme } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Layout } from '@components/Layout';
import { useGetJobApplicationDetail } from '../hooks/useGetJobApplicationDetail';
import BrandCard from './BrandCard';
import ProCard from './ProCard';
import JobOfferCard from './JobOfferCard';
import TalentCard from './TalentCard';
import ResumeViewer from './ResumeViewer';
import TalentContactSection from './TalentContactSection';

const DetailSkeleton = () => (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rounded" height={96} />
            ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
            <Skeleton variant="rounded" height={600} />
            <Skeleton variant="rounded" height={400} />
        </Box>
    </Box>
);

const JobApplicationDetailModule = () => {
    const { id } = useParams<{ id: string }>();
    const { application, loading, refetch } = useGetJobApplicationDetail(id);
    const theme = useTheme();

    const talent = application?.talent;
    const jobOffer = application?.jobOffer;
    const pro = application?.pro;
    const brand = jobOffer?.brand;

    const resumeUrl = application?.resumeFile?.url || application?.resume || talent?.resume?.path;
    const resumeFileId = application?.resumeFile?._id ?? null;

    const headingText = loading
        ? '...'
        : [talent?.firstName, talent?.lastName].filter(Boolean).join(' ') +
          (jobOffer?.title ? ` — ${jobOffer.title}` : '');

    return (
        <Layout
            breadcrumbs={[
                { label: 'Dashboard', href: '/', value: 'dashboard' },
                { label: 'Candidatures', href: '/applications', value: 'applications' },
                {
                    label: application?.jobApplicationNumber ?? 'Détail',
                    href: '#',
                    value: 'detail',
                },
            ]}
            heading={headingText}
        >
            {loading ? (
                <DetailSkeleton />
            ) : (
                <Box sx={{ p: 3 }}>
                    {/* Back button */}
                    <Button
                        component={Link}
                        to="/applications"
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        sx={{ mb: 3, color: theme.palette.text.secondary }}
                    >
                        Retour aux candidatures
                    </Button>

                    {/* Summary cards */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        {brand && <BrandCard brand={brand} />}
                        {pro && <ProCard pro={pro} />}
                        {jobOffer && <JobOfferCard jobOffer={jobOffer} />}
                        {talent && <TalentCard talent={talent} />}
                    </Box>

                    {/* Body */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                            gap: 3,
                            alignItems: 'start',
                        }}
                    >
                        <ResumeViewer
                            resumeUrl={resumeUrl}
                            fileId={resumeFileId}
                            talentId={talent?._id}
                            onUploadSuccess={() => refetch()}
                        />
                        <TalentContactSection talent={talent} application={application} />
                    </Box>
                </Box>
            )}
        </Layout>
    );
};

export default JobApplicationDetailModule;
